import React, { useState } from 'react';
import { WarningComponent } from 'ui/Icons';
import { SvgIcon } from 'ui/SvgIcon';
import AnimateHeight from 'react-animate-height';
import { HtmlStringRenderer } from 'components/HtmlStringRenderer';
import classNames from 'classnames';

export interface IAnnouncementItem {
  uuid: string;
  title: string;
  description: string;
}

export interface IAnnouncementsDisplayRenderProps {
  announcements: IAnnouncementItem[];
  className?: string;
  maxInitialDisplay?: number; // Default 3
  titleClassName?: string;
}

/**
 * Presentational component to display announcements with show more/less functionality.
 * This component does NOT fetch data - it receives announcements as props.
 * Shows max 3 titles initially, with "Show more" to expand and show all titles + descriptions.
 */
export const AnnouncementsDisplayRender: React.FC<IAnnouncementsDisplayRenderProps> = ({
  announcements,
  className,
  maxInitialDisplay = 3,
  titleClassName,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const displayAnnouncements = isExpanded ? announcements : announcements.slice(0, maxInitialDisplay);

  if (announcements.length === 0) {
    return null;
  }

  return (
    <div
      className={classNames(
        'p-10px mt-25px bg-yellow-advert-light border border-yellow-advert rounded flex flex-col gap-5px',
        className
      )}
    >
      <div className="flex items-center gap-5px">
        <SvgIcon IconComponent={WarningComponent} width="20px" height="20px" />
        <p className={classNames('text-brown-prime text-15px font-pt-sans my-0 flex-1 uppercase', titleClassName)}>
          Important Announcements ({announcements.length})
        </p>
      </div>
      <ul className="flex flex-col gap-5px">
        {displayAnnouncements.map(announcement => (
          <li key={announcement.uuid} className="px-8px">
            <div className="font-bold text-black text-15px !font-pt-sans">{announcement.title}</div>
            <AnimateHeight duration={300} height={isExpanded ? 'auto' : 0} className="accordion-area">
              <HtmlStringRenderer
                content={announcement.description}
                className="text-black text-15px mt-1 !font-pt-sans"
              />
            </AnimateHeight>
          </li>
        ))}
      </ul>
      <div
        className="text-brown-prime text-15px pl-29px cursor-pointer font-pt-sans underline"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? 'Show less' : 'Show more'}
      </div>
    </div>
  );
};
